import {
  Children,
  ComponentPropsWithoutRef,
  ReactElement,
  cloneElement,
  forwardRef,
} from "react";
import cx from "classnames";
import ChevronRight from "@servicetitan/hammer-icon/mdi/filled/chevron_right.svg";

import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil } from "../../internal/hooks";
import { Icon, Text } from "../";

import styles from "./Breadcrumbs.module.scss";
import { BreadcrumbsLink, BreadcrumbsLinkProps } from "./BreadcrumbsLink";

/**
 * Props for the Breadcrumbs component
 * @extends ComponentPropsWithoutRef<"nav">
 * @extends LayoutUtilProps
 */
export type BreadcrumbsProps = ComponentPropsWithoutRef<"nav"> &
  LayoutUtilProps & {
    /**
     * Breadcrumb links to display. The last child will be rendered as non-clickable text.
     */
    children:
      | ReactElement<BreadcrumbsLinkProps>
      | ReactElement<BreadcrumbsLinkProps>[];
  };

/**
 * Breadcrumbs component for displaying navigation hierarchy.
 *
 * Features:
 * - Displays a series of navigation links with chevron separators
 * - Automatically renders the last item as non-clickable text
 * - Includes proper ARIA labeling for accessibility
 * - Supports layout utilities for positioning and spacing
 * - Uses chevron icons as visual separators between links
 * - Responsive design with flexbox layout
 *
 * @example
 * <Breadcrumbs>
 *   <Breadcrumbs.Link href="/home">Home</Breadcrumbs.Link>
 *   <Breadcrumbs.Link href="/products">Products</Breadcrumbs.Link>
 *   <Breadcrumbs.Link href="/electronics">Electronics</Breadcrumbs.Link>
 * </Breadcrumbs>
 */
export const Breadcrumbs = Object.assign(
  forwardRef<HTMLElement, BreadcrumbsProps>(
    function BreadcrumbsInner(props, ref) {
      const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
      const { children, className, style, ...rest } = componentProps;

      const classes = cx([styles["breadcrumbs"]], className, {});

      const styleCombined = {
        ...style,
        ...layoutStyles,
      };

      const linkCount = Children.count(children);

      return (
        <nav
          data-anv="breadcrumbs"
          style={styleCombined}
          className={classes}
          aria-label="Breadcrumb"
          {...rest}
          ref={ref}
        >
          {Children.map(children, (link, i) => {
            if (i + 1 === linkCount) {
              return (
                <Text
                  inline
                  size="small"
                  style={{
                    fontWeight: "bold",
                  }}
                >
                  {link.props.children}
                </Text>
              );
            } else {
              return (
                <>
                  {cloneElement(link as ReactElement)}
                  <Icon svg={ChevronRight} aria-hidden="true" />
                </>
              );
            }
          })}
        </nav>
      );
    },
  ),
  {
    /**
     * BreadcrumbsLink component for individual breadcrumb navigation items.
     *
     * Features:
     * - Renders as a clickable link with secondary appearance
     * - Supports all standard anchor element props
     * - Includes layout utilities for positioning and spacing
     * - Uses small text size for consistent styling
     * - Fully accessible with proper focus management
     * - Automatically applies secondary link styling
     *
     * @example
     * <Breadcrumbs.Link href="/dashboard">Dashboard</Breadcrumbs.Link>
     */
    Link: BreadcrumbsLink,
  },
);
Breadcrumbs.displayName = "Breadcrumbs";
